// The pieces every notification face renderer is built from (`notification-copy.ts`,
// `notification-copy-escalations.ts`): one renderer per (kind, face), each a zod parse
// of exactly its producer's payload, `null` when the payload does not parse.

import type { Translator } from '@developerz/i18n';
import { z } from 'zod';
import type { NotificationRow } from './notifications';

/** A row's title and body in the reader's language, or `null`: show the server's text. */
export type NotificationCopy = { readonly title: string; readonly body: string };

type Payload = Record<string, unknown>;

/** One face: the row's copy from its payload, or `null` for the server's own text. */
export type Renderer = (
  payload: Payload,
  tr: Translator,
  row: NotificationRow,
) => NotificationCopy | null;

export const count = z.number().int().nonnegative();
export const text = z.string().min(1);

/** Parse `payload` with `schema` and spell it, or `null` when it does not parse. */
export function face<S extends z.ZodType>(
  schema: S,
  spell: (args: z.infer<S>, tr: Translator, row: NotificationRow) => NotificationCopy | null,
): Renderer {
  return (payload, tr, row) => {
    const parsed = schema.safeParse(payload);
    return parsed.success ? spell(parsed.data, tr, row) : null;
  };
}

/** A phase-1 face's optional `face` stamp, once its producer writes one. */
export const stamp = (name: string) => z.literal(name).optional();
